import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { PlayerImageSchema, PlayerSchema } from '../players/api'
import { TeamLogoSchema } from '../assets/api'
import { TeamListSchema } from '../teams/api'

const getCompetitionStats = async ({
  competition_id,
}: {
  competition_id: number
}) => {
  const response = await axios.get(`/api/competitions/${competition_id}/stats`)
  return CompetitionStatsSchema.parse(response.data)
}

export const useCompetitionStats = ({
  competition_id,
}: {
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['competitionStats', { competition_id }],
    queryFn: () => getCompetitionStats({ competition_id }),
  })
}

async function getTeamStats({
  team_id,
  competition_id,
}: {
  team_id: number | null
  competition_id: number
}) {
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams/${team_id}/stats`
  )
  return TeamStatsSchema.parse(response.data)
}

export const useTeamStats = ({
  team_id,
  competition_id,
}: {
  team_id: number | null
  competition_id: number
}) => {
  return useQuery({
    queryKey: ['teamStats', { team_id, competition_id }],
    queryFn: () => getTeamStats({ team_id, competition_id }),
    enabled: team_id !== null,
  })
}

export const TeamStatsSchema = z.object({
  team_id: z.number(),
  team_name: z.string(),
  rank: z.number(),
})

export const CompetitionStatsSchema = z.object({
  num_teams: z.number(),
  num_players: z.number(),
  overall_rating: z.number().nullable(),
})

export const TopPlayerSchema = z.object({
  rank: z.number(),
  player_id: z.number(),
  name: z.string(),
  photo: PlayerImageSchema,
  rating: z.number(),
  team_name: z.string().optional(),
  team_logo: TeamLogoSchema.optional(),
})

export const TopTeamSchema = z.object({
  rank: z.number(),
  team_id: z.number(),
  name: z.string(),
  logo: TeamLogoSchema,
  rating: z.number(),
})

export const ComparePlayerSchema = z.object({
  player_id: PlayerSchema.shape.player_id,
  user_id: PlayerSchema.shape.user_id,
  player_name: PlayerSchema.shape.name,
  player_photo: PlayerSchema.shape.photo,
})

export const CompareTeamSchema = z.object({
  team_id: TeamListSchema.shape.team_id,
  team_name: TeamListSchema.shape.name,
  team_logo: TeamListSchema.shape.logo,
  players: z.array(ComparePlayerSchema),
})

export type CompetitionTeam = z.infer<typeof CompareTeamSchema>
export type CompetitionPlayer = z.infer<typeof ComparePlayerSchema>
export type CompetitionStats = z.infer<typeof CompetitionStatsSchema>
export type TopPlayer = z.infer<typeof TopPlayerSchema>
export type TopTeam = z.infer<typeof TopTeamSchema>
